import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';

interface FavoriteEventButtonProps {
  eventId: string;
  className?: string;
  showText?: boolean;
}

export function FavoriteEventButton({ eventId, className = '', showText = false }: FavoriteEventButtonProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: ReturnType<typeof supabase.channel>;

    async function checkFavoriteStatus() {
      if (!user || !profile) {
        if (mounted) {
          setLoading(false);
        }
        return;
      }

      try {
        // Check if event is already favorited
        const { data: existingFavorite } = await supabase
          .from('favorite_events')
          .select('*')
          .eq('event_id', eventId)
          .eq('user_id', profile.id)
          .maybeSingle();

        if (mounted) {
          setIsFavorite(!!existingFavorite);
          setLoading(false);
        }

        // Subscribe to changes for this specific event favorite
        subscription = supabase.channel(`favorite_events:${eventId}:${profile.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'favorite_events',
              filter: `event_id=eq.${eventId}`,
            },
            (payload) => {
              if (!mounted) return;

              // Update favorite status based on the change
              if (payload.eventType === 'INSERT' && payload.new?.user_id === profile.id) {
                setIsFavorite(true);
              } else if (payload.eventType === 'DELETE' && payload.old?.user_id === profile.id) {
                setIsFavorite(false);
              }
            }
          )
          .subscribe();
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    }

    checkFavoriteStatus();

    return () => {
      mounted = false;
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [eventId, user, profile]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user || !profile || loading) return;

    try {
      setLoading(true);

      if (isFavorite) {
        // Remove from favorites - optimistically update UI
        setIsFavorite(false);

        const { error } = await supabase
          .from('favorite_events')
          .delete()
          .eq('event_id', eventId)
          .eq('user_id', profile.id);

        if (error) {
          // Revert optimistic update on error
          setIsFavorite(true);
          throw error;
        }
      } else {
        // Add to favorites - optimistically update UI
        setIsFavorite(true);

        // Check if favorite already exists to prevent duplicates
        const { data: existingFavorite } = await supabase
          .from('favorite_events')
          .select('*')
          .eq('event_id', eventId)
          .eq('user_id', profile.id)
          .maybeSingle();

        if (!existingFavorite) {
          const { error } = await supabase
            .from('favorite_events')
            .insert([{ event_id: eventId, user_id: profile.id }]);

          if (error) {
            // Revert optimistic update on error
            setIsFavorite(false);
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={!user || loading}
      className={`flex items-center space-x-2 text-sm ${
        isFavorite ? 'text-primary' : 'text-light/60 hover:text-primary'
      } ${
        showText ? 'btn bg-dark/10' : ''
      } transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      title={isFavorite ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}
    >
      <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
      {showText && <span>{isFavorite ? t('favorites.favorited') : t('favorites.favorite')}</span>}
    </button>
  );
}